/**
 * Shared component that wraps TranscriptVirtualList with tree flattening,
 * collapse state, turn-map computation, keyboard navigation, and imperative
 * scroll-to-event/index. Apps provide collapse state via callback props.
 */

import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { StickyScrollProvider } from "@tsmono/react/components";
import { useLatestRef, useProperty, useScrollTrack } from "@tsmono/react/hooks";
import type { VirtualListHandle } from "@tsmono/react/virtual";

import type { RowSelection } from "../selection/rowSelection";
import { useListRowSelection } from "../selection/useListRowSelection";

import { GoToTurnBar, type GoToTurnBarHandle } from "./GoToTurnBar";
import { useTranscriptKeyboardNavigation } from "./hooks/useTranscriptKeyboardNavigation";
import { isSelectableEvent } from "./selection/transcriptSelection";
import { TranscriptVirtualList } from "./TranscriptVirtualList";
import { findCollapsedAncestors, flatTree } from "./transform/flatten";
import { pairToolApprovals } from "./transform/toolApprovals";
import { pairToolSentinels } from "./transform/toolSentinels";
import {
  anchorIndexForEvent,
  anchorIndexForTurn,
  computeTranscriptTurns,
} from "./turnNavigation";
import type { EventNode, EventNodeContext, EventPanelCallbacks } from "./types";

// =============================================================================
// Types
// =============================================================================

export interface TranscriptViewNodesProps {
  id: string;
  eventNodes: EventNode[];
  defaultCollapsedIds: Record<string, boolean>;
  /** Whether events are still being streamed (enables auto-follow scroll). */
  running?: boolean;
  /** Whether the sample's event backlog is still loading (live sample). */
  backfilling?: boolean;
  /** Whether a live→finished transition may scroll the view to the top —
   *  false for unsuccessful finishes (error/cancelled). */
  scrollToTopOnFinish?: boolean;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  initialEventId?: string | null;
  /** Optional message ID — after scrolling to the containing event, also
   *  query the DOM for the message and adjust scroll so the message itself
   *  lands at the top of the visible area. */
  initialMessageId?: string | null;
  /** An explicit `follow=1` URL param: arm live-tail at mount even though a
   *  deep link (`?event=`/`?message=`) also makes this a nav-owned mount. */
  followRequested?: boolean;
  offsetTop?: number;
  className?: string;
  renderAgentCard?: (node: EventNode, className?: string) => ReactNode;
  getEventUrl?: (eventId: string) => string | undefined;
  linkingEnabled?: boolean;
  /** Builds the focus-mode entry href for the header's focus control
   *  (plain click enters in-window; modified clicks open a new tab). Omit to
   *  hide that control. */
  getEventFocusUrl?: (
    eventId: string,
    selectedTab?: string
  ) => string | undefined;
  /** Navigate to a focus URL in the current window. */
  onOpenEventFocus?: (focusRoute: string) => void;

  // Collapse state (app provides via its store, already scope-specific)
  collapsedTranscript?: Record<string, boolean>;
  onCollapseTranscript?: (nodeId: string, collapsed: boolean) => void;
  /** Expand several nodes in one state update — used to reveal deep-link
   *  targets hidden inside collapsed regions. */
  onExpandNodes?: (nodeIds: string[]) => void;
  /** Extra context fields merged into every EventNodeContext entry. */
  eventNodeContext?: Partial<EventNodeContext>;
  /** Called before this list's own programmatic scrolls so the swimlane
   *  headroom doesn't flicker mid-scroll; `true` engages the debounced lock. */
  onProgrammaticScroll?: (debounce?: boolean) => void;
  /** Force the chrome headroom to the state an equivalent manual scroll would
   *  produce: nav/deep-link landings collapse it, `k` past turn 1 re-expands. */
  onHeadroomSetHidden?: (hidden: boolean) => void;
  /** `h` — select the previous agent lane (driven by the parent's swimlane
   *  selection so the lane highlight + scoping update too). */
  onPrevAgent?: () => void;
  /** `l` — select the next agent lane. */
  onNextAgent?: () => void;
  /** Called when an explicit turn navigation lands (j/k, chevrons, editable
   *  number) so the app can reflect the turn in the URL (`?event=`, replace) —
   *  like an outline click. NOT called on passive scroll. */
  onNavigatedToEvent?: (eventId: string) => void;
  /** Disable turn/agent keyboard nav while find-in-page owns the keyboard. */
  keyboardNavDisabled?: boolean;
  /** Host-owned evidence selection; present only while selection mode is on. */
  selection?: RowSelection;
}

export interface TranscriptViewNodesHandle {
  /** Scroll to an event by its ID. Expands collapsed ancestors first if needed. */
  scrollToEvent: (eventId: string) => void;
  /** Read-only accessor for the current flattened nodes (post-collapse filter). */
  getFlattenedNodes: () => EventNode[];
  /** Read-only accessor for the virtual list's currently-rendered visible range. */
  getVisibleRange: () => { startIndex: number; endIndex: number };
}

// =============================================================================
// Imperative scroll helpers
// =============================================================================

const escapeAttr = (id: string): string =>
  typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(id)
    : id.replace(/"/g, '\\"');

// =============================================================================
// Component
// =============================================================================

export const TranscriptViewNodes = forwardRef<
  TranscriptViewNodesHandle,
  TranscriptViewNodesProps
>(function TranscriptViewNodes(
  {
    id,
    eventNodes,
    defaultCollapsedIds,
    running,
    backfilling,
    scrollToTopOnFinish,
    scrollRef,
    initialEventId,
    initialMessageId,
    followRequested,
    offsetTop = 10,
    className,
    renderAgentCard,
    getEventUrl,
    linkingEnabled,
    getEventFocusUrl,
    onOpenEventFocus,
    collapsedTranscript,
    onCollapseTranscript,
    onExpandNodes,
    eventNodeContext,
    onProgrammaticScroll,
    onHeadroomSetHidden,
    onPrevAgent,
    onNextAgent,
    onNavigatedToEvent,
    keyboardNavDisabled,
    selection,
  },
  ref
) {
  const listHandle = useRef<VirtualListHandle | null>(null);

  // This mount is nav-owned when it lands on a deep link (`?event=`/`?message=`)
  // — the same signal the app uses to seed its chrome-headroom / nav-ownership.
  // Frozen at mount: runtime `?event=` writes from j/k navigation are handled
  // imperatively and must NOT re-assert stand-down. Forwarded to VirtualList so
  // live-tail follow stands down at mount, letting the landing win (unless
  // `followRequested`), and only arms afterwards from an explicit act.
  const [navOwned] = useState(() => !!(initialEventId || initialMessageId));

  // The transcript's live-tail state (VirtualList's `follow`, keyed by the same
  // persistenceKey). VirtualList seeds this at mount (its effective initial
  // follow) and drives it thereafter, so it is the ONE source of truth — read
  // it exactly, never guess from `running`. The setter lets `j` past the last
  // turn arm follow (the transcript analog of focus-mode's step-past-last arm).
  const [transcriptFollow, setTranscriptFollow] = useProperty<boolean | null>(
    id,
    "follow",
    { defaultValue: null }
  );

  const getCollapsed = useCallback(
    (nodeId: string) => {
      return (collapsedTranscript || defaultCollapsedIds)[nodeId] === true;
    },
    [collapsedTranscript, defaultCollapsedIds]
  );

  // Pair each ApprovalEvent to its ToolEvent by call.id, so ToolEventView
  // can render the approval inline without nesting it in the tree (which
  // would give the tool panel a bogus expand chevron).
  const { toolApprovals, hiddenApprovalIds, approvalScrollRedirects } = useMemo(
    () => pairToolApprovals(eventNodes),
    [eventNodes]
  );
  const {
    toolSentinels,
    standaloneSentinels,
    hiddenSentinelIds,
    sentinelScrollRedirects,
  } = useMemo(() => pairToolSentinels(eventNodes), [eventNodes]);

  // Hidden approvals and sentinel events have no row of their own — retarget
  // navigation at the row that renders them inline.
  const redirectEventId = useCallback(
    (eventId: string) =>
      approvalScrollRedirects.get(eventId) ??
      sentinelScrollRedirects.get(eventId) ??
      eventId,
    [approvalScrollRedirects, sentinelScrollRedirects]
  );
  const scrollEventId = initialEventId
    ? redirectEventId(initialEventId)
    : initialEventId;

  const flattenedNodes = useMemo(() => {
    const all = flatTree(
      eventNodes,
      collapsedTranscript || defaultCollapsedIds
    );
    return hiddenApprovalIds.size === 0 && hiddenSentinelIds.size === 0
      ? all
      : all.filter(
          (n) => !hiddenApprovalIds.has(n.id) && !hiddenSentinelIds.has(n.id)
        );
  }, [
    eventNodes,
    collapsedTranscript,
    defaultCollapsedIds,
    hiddenApprovalIds,
    hiddenSentinelIds,
  ]);

  const mergedEventNodeContext = useMemo<Partial<EventNodeContext>>(
    () => ({
      ...eventNodeContext,
      toolApprovals,
      toolSentinels,
      standaloneSentinels,
    }),
    [eventNodeContext, toolApprovals, toolSentinels, standaloneSentinels]
  );

  // Ranges span the VISIBLE rows, as the user sees them.
  const rowSelection = useListRowSelection(selection, () =>
    flattenedNodes.filter((n) => isSelectableEvent(n.event)).map((n) => n.id)
  );

  // Turn map + anchors from the shared helper (collapse-independent
  // numbering; same event-type filtering as the sidebar).
  const { computedTurnMap, turnAnchorIds, anchorIdByTurn } = useMemo(() => {
    const {
      turnMap: computed,
      anchorIds,
      anchorIdByTurn: byTurn,
    } = computeTranscriptTurns(eventNodes, flattenedNodes, defaultCollapsedIds);
    return {
      computedTurnMap: computed,
      turnAnchorIds: anchorIds,
      anchorIdByTurn: byTurn,
    };
  }, [eventNodes, flattenedNodes, defaultCollapsedIds]);

  const flattenedNodesLatest = useLatestRef<EventNode[]>(flattenedNodes);

  // Latest turn lookup for scrollRowToTop's stamp: an expansion retry lands
  // after the turn map is recomputed, so closed-over values may be stale.
  const turnLookupLatest = useLatestRef({ turnAnchorIds, computedTurnMap });

  // The turn at the top of the viewport (tracked by useScrollTrack below);
  // seeded from the deep-link target so a keypress can't race the tracker's
  // first report (e.g. the "double f" right after a route transition).
  // Lazily seeded: a useRef initializer expression would re-run the
  // O(#turns) scan on every render just to be discarded.
  const turnIndexSeededRef = useRef(false);
  const currentTurnIndexRef = useRef(-1);
  if (!turnIndexSeededRef.current) {
    turnIndexSeededRef.current = true;
    currentTurnIndexRef.current = anchorIndexForEvent(
      turnAnchorIds,
      computedTurnMap,
      scrollEventId
    );
  }

  // The one scroll primitive for every programmatic row scroll — and the
  // single choke point stamping the current turn, so a j/k/f fired before
  // the scroll tracker's next report steps from the declared target.
  const scrollRowToTop = useCallback(
    (index: number, onDone?: () => void) => {
      onProgrammaticScroll?.(true);
      const rowId = flattenedNodesLatest.current[index]?.id;
      if (rowId !== undefined) {
        const lookup = turnLookupLatest.current;
        currentTurnIndexRef.current = anchorIndexForEvent(
          lookup.turnAnchorIds,
          lookup.computedTurnMap,
          rowId
        );
      }
      const handle = listHandle.current;
      if (!handle) {
        onDone?.();
        return;
      }
      handle.scrollToIndex({
        index,
        align: "start",
        behavior: "auto",
        onDone,
      });
    },
    [onProgrammaticScroll, flattenedNodesLatest, turnLookupLatest]
  );

  const scrollToEvent = useCallback(
    (targetId: string) => {
      const eventId = redirectEventId(targetId);
      // Imperative jumps are navigation too: collapse the chrome even when
      // the URL doesn't change (re-click) and the deep-link effect won't run.
      onHeadroomSetHidden?.(true);
      const tryScroll = (idx: number) => {
        if (listHandle.current) {
          scrollRowToTop(idx);
        } else {
          onProgrammaticScroll?.(true);
          const el = scrollRef?.current?.querySelector(
            `[id="${escapeAttr(eventId)}"]`
          );
          el?.scrollIntoView({ block: "start", behavior: "auto" });
        }
      };

      const idx = flattenedNodes.findIndex((e) => e.id === eventId);
      if (idx !== -1) {
        tryScroll(idx);
        return;
      }

      // Hidden in a collapsed region: expand ancestors in ONE batched update
      // (sequential toggles clobber each other while the store is unseeded),
      // then retry once the flattened list updates.
      const collapsedAncestors = findCollapsedAncestors(
        eventNodes,
        eventId,
        collapsedTranscript ?? defaultCollapsedIds
      );
      if (collapsedAncestors.length > 0 && onExpandNodes) {
        onExpandNodes(collapsedAncestors);
        // After the next render, the flat list will include the event.
        // Retry up to 3 frames in case React batching pushes the commit later.
        const retryScroll = (attemptsLeft: number) => {
          const newIdx = flattenedNodesLatest.current.findIndex(
            (e) => e.id === eventId
          );
          if (newIdx !== -1) {
            tryScroll(newIdx);
            return;
          }
          if (attemptsLeft > 0) {
            requestAnimationFrame(() => retryScroll(attemptsLeft - 1));
          }
        };
        requestAnimationFrame(() => retryScroll(3));
        return;
      }

      // Fall back to direct DOM scroll.
      onProgrammaticScroll?.(true);
      const el = scrollRef?.current?.querySelector(
        `[id="${escapeAttr(eventId)}"]`
      );
      el?.scrollIntoView({ block: "start", behavior: "auto" });
    },
    [
      redirectEventId,
      flattenedNodes,
      eventNodes,
      collapsedTranscript,
      defaultCollapsedIds,
      onExpandNodes,
      scrollRowToTop,
      scrollRef,
      onProgrammaticScroll,
      onHeadroomSetHidden,
      flattenedNodesLatest,
    ]
  );

  const visibleRangeRef = useRef<{ startIndex: number; endIndex: number }>({
    startIndex: 0,
    endIndex: 0,
  });

  useImperativeHandle(
    ref,
    () => ({
      scrollToEvent,
      getFlattenedNodes: () => flattenedNodesLatest.current,
      getVisibleRange: () => visibleRangeRef.current,
    }),
    [scrollToEvent, flattenedNodesLatest]
  );

  // Expand a deep-link target's collapsed ancestors, once per target so a
  // stale URL param doesn't fight the user re-collapsing.
  const expandedForTargetRef = useRef<string | null>(null);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!scrollEventId || !onExpandNodes) return;
    if (expandedForTargetRef.current === scrollEventId) return;
    if (flattenedNodes.some((n) => n.id === scrollEventId)) return;
    const collapsedAncestors = findCollapsedAncestors(
      eventNodes,
      scrollEventId,
      collapsedTranscript ?? defaultCollapsedIds
    );
    if (collapsedAncestors.length === 0) return;
    expandedForTargetRef.current = scrollEventId;
    onExpandNodes(collapsedAncestors);
  }, [
    scrollEventId,
    onExpandNodes,
    flattenedNodes,
    eventNodes,
    collapsedTranscript,
    defaultCollapsedIds,
  ]);

  // Runtime URL→event scroll (deep link / outline / sidebar turn link). Dedup by
  // target so re-renders from filter/collapse don't re-fire an already-handled
  // jump.
  const lastScrolledKeyRef = useRef<string | null>(null);
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!scrollEventId) {
      lastScrolledKeyRef.current = null;
      return;
    }
    const targetKey = `${scrollEventId}:${initialMessageId ?? ""}`;
    if (lastScrolledKeyRef.current === targetKey) return;
    const idx = flattenedNodes.findIndex((n) => n.id === scrollEventId);
    if (idx === -1) return;
    lastScrolledKeyRef.current = targetKey;
    // A deep-link landing ALWAYS collapses the chrome — target-position
    // special cases proved log-shape-dependent.
    onHeadroomSetHidden?.(true);
    // `?message=` deep link: settle the cited message below the chrome for up
    // to 90 frames while late layout (markdown/code/images) reflows.
    const container = scrollRef?.current;
    if (!initialMessageId || !container) {
      scrollRowToTop(idx);
      return;
    }
    const selector = `[data-message-id="${escapeAttr(initialMessageId)}"]`;
    const eventRowSelector = `[id="${escapeAttr(scrollEventId)}"]`;
    // Park the message below the event's opaque sticky header (not just the
    // outer chrome) or the header occludes the message's first lines.
    const stuckHeaderHeight = () =>
      container
        .querySelector<HTMLElement>(eventRowSelector)
        ?.querySelector<HTMLElement>("[data-sticky-stuck]")
        ?.getBoundingClientRect().height ?? 0;
    let raf = 0;
    let cancelled = false;
    let lastTarget: number | null = null;
    let attempts = 0;
    // User input during the parking loop means they've taken over — stop
    // (symmetric with settleScrollToIndex's userInteracting abort).
    const abort = () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
    container.addEventListener("wheel", abort, { passive: true });
    container.addEventListener("touchmove", abort, { passive: true });
    container.addEventListener("mousedown", abort, { passive: true });
    container.addEventListener("keydown", abort, { passive: true });
    const settle = () => {
      if (cancelled) return;
      attempts += 1;
      const el = container.querySelector<HTMLElement>(selector);
      if (el) {
        const delta =
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          offsetTop -
          stuckHeaderHeight();
        const target = container.scrollTop + delta;
        if (lastTarget === null || Math.abs(target - lastTarget) > 2) {
          lastTarget = target;
          container.scrollTop = target;
        }
      }
      if (attempts < 90) {
        raf = requestAnimationFrame(settle);
      }
    };
    scrollRowToTop(idx, () => {
      if (!cancelled) raf = requestAnimationFrame(settle);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      container.removeEventListener("wheel", abort);
      container.removeEventListener("touchmove", abort);
      container.removeEventListener("mousedown", abort);
      container.removeEventListener("keydown", abort);
    };
  }, [
    scrollEventId,
    initialMessageId,
    flattenedNodes,
    scrollRowToTop,
    scrollRef,
    offsetTop,
    onHeadroomSetHidden,
  ]);

  // ---------------------------------------------------------------------------
  // Turn navigation (j/k + header chevrons)
  // ---------------------------------------------------------------------------

  const goToTurn = useCallback(
    (index: number, onDone?: () => void) => {
      if (turnAnchorIds.length === 0) {
        onDone?.();
        return;
      }
      const clamped = Math.max(0, Math.min(turnAnchorIds.length - 1, index));
      const flatIdx = flattenedNodesLatest.current.findIndex(
        (n) => n.id === turnAnchorIds[clamped]
      );
      if (flatIdx === -1) {
        onDone?.();
        return;
      }
      // Collapse the chrome with the jump; the settle re-issues the jump each
      // frame, so the collapse's height change is re-corrected until stable.
      onHeadroomSetHidden?.(true);
      scrollRowToTop(flatIdx, onDone);
      const anchorId = turnAnchorIds[clamped];
      if (anchorId && onNavigatedToEvent) {
        // Prime the deep-link dedup so the resulting ?event= change doesn't
        // re-scroll (we've already scrolled).
        lastScrolledKeyRef.current = `${anchorId}:`;
        onNavigatedToEvent(anchorId);
      }
    },
    [
      turnAnchorIds,
      scrollRowToTop,
      onNavigatedToEvent,
      onHeadroomSetHidden,
      flattenedNodesLatest,
    ]
  );

  // `k` past turn 1: return to the very top of the transcript (the pre-turn
  // preamble) and re-expand the chrome, exactly as a manual scroll to the top
  // would. The scroll tracker then confirms the "above turn 1" index.
  const goToVeryTop = useCallback(() => {
    onProgrammaticScroll?.(true);
    // Instant, not smooth: the expand below is gated on actually being at the
    // top, which a smooth scroll only reaches many frames later.
    listHandle.current?.scrollTo({ top: 0, behavior: "auto" });
    onHeadroomSetHidden?.(false);
    currentTurnIndexRef.current = -1;
  }, [onProgrammaticScroll, onHeadroomSetHidden]);

  const onNext = useCallback(() => {
    const nextIndex = currentTurnIndexRef.current + 1;
    // One more `j` past the last turn of a RUNNING sample arms live-tail
    // follow — the transcript analog of focus-mode's step-past-last arm (see
    // useFocusTurnNavigation.stepTurn) and of scrolling to the bottom. Writing
    // the follow store true is an explicit act, so it survives the nav-owned
    // stand-down that governs the mount; VirtualList picks it up and tails.
    // Manual scroll away or `k` disarms (via VirtualList's scroll handler).
    if (
      running &&
      turnAnchorIds.length > 0 &&
      nextIndex >= turnAnchorIds.length
    ) {
      if (transcriptFollow !== true) setTranscriptFollow(true);
      return;
    }
    goToTurn(nextIndex);
  }, [
    goToTurn,
    running,
    turnAnchorIds.length,
    transcriptFollow,
    setTranscriptFollow,
  ]);
  // Synchronous index decision, mirroring `onNext` — a confirm/settle round
  // trip here made held/rapid `k` presses race the settle and lose steps.
  const goToPrevFrom = useCallback(
    (idx: number) => {
      if (idx <= 0) {
        goToVeryTop();
        return;
      }
      goToTurn(idx - 1);
    },
    [goToTurn, goToVeryTop]
  );
  const onPrev = useCallback(() => {
    // `k` while live-following disarms follow — exactly symmetric with
    // onNext's arm-past-last branch and consistent with focus-mode's stepTurn
    // disarm (see useFocusTurnNavigation). One synchronous store write, before
    // the scroll step; without it the next streamed event snaps the view back
    // to the tail. (Manual scroll also disarms via VirtualList; this is the
    // keyboard-step analog.)
    if (running && transcriptFollow === true) setTranscriptFollow(false);
    goToPrevFrom(currentTurnIndexRef.current);
  }, [goToPrevFrom, running, transcriptFollow, setTranscriptFollow]);

  const goToTurnBarRef = useRef<GoToTurnBarHandle | null>(null);

  // The event card a go-to-turn-bar jump landed on keeps a persistent
  // selection ring until the next bar jump.
  const [jumpTargetId, setJumpTargetId] = useState<string | null>(null);

  // Per-event-header turn controls: several headers can be visible at once,
  // so each steps from ITS OWN turn number, never the shared current turn.
  const onPrevTurn = useCallback(
    (turnNumber: number) => {
      goToPrevFrom(
        anchorIndexForTurn(turnAnchorIds, computedTurnMap, turnNumber)
      );
    },
    [turnAnchorIds, computedTurnMap, goToPrevFrom]
  );
  const onNextTurn = useCallback(
    (turnNumber: number) => {
      goToTurn(
        anchorIndexForTurn(turnAnchorIds, computedTurnMap, turnNumber) + 1
      );
    },
    [turnAnchorIds, computedTurnMap, goToTurn]
  );
  const onTurnLabelClick = useCallback((turnNumber: number) => {
    goToTurnBarRef.current?.open(turnNumber);
  }, []);

  // A user switched an event's inner tab: land its row top under the sticky
  // chrome via the same primitive j/k use, so the new tab starts from its top.
  const onTabSelected = useCallback(
    (eventNodeId: string) => {
      const idx = flattenedNodesLatest.current.findIndex(
        (n) => n.id === eventNodeId
      );
      if (idx !== -1) scrollRowToTop(idx);
    },
    [scrollRowToTop, flattenedNodesLatest]
  );

  // Last-known selected tab per anchor panel, reported by EventPanel, so the
  // keyboard `f` entry carries the same tab as the panel's header link.
  const anchorTabsRef = useRef(new Map<string, string>());
  const onFocusTabChange = useCallback(
    (eventNodeId: string, tabName: string | undefined) => {
      if (tabName === undefined) anchorTabsRef.current.delete(eventNodeId);
      else anchorTabsRef.current.set(eventNodeId, tabName);
    },
    []
  );

  // Entering focus while the transcript is live-tailing carries the armed
  // follow state (`follow=1`) so focus keeps following; one wrapper covers both
  // entries (`f` key, header icon). Conservative: arm ONLY when follow is
  // explicitly true — reading at the top of a running sample must not carry it.
  const getEventFocusUrlArmed = useMemo(() => {
    if (!getEventFocusUrl) return getEventFocusUrl;
    const following = transcriptFollow === true;
    return (eventId: string, selectedTab?: string) => {
      const href = getEventFocusUrl(eventId, selectedTab);
      if (!href || !running || !following) return href;
      return `${href}${href.includes("?") ? "&" : "?"}follow=1`;
    };
  }, [getEventFocusUrl, running, transcriptFollow]);

  const eventCallbacks = useMemo<EventPanelCallbacks>(
    () => ({
      onCollapse: onCollapseTranscript,
      getCollapsed,
      getEventUrl,
      linkingEnabled,
      getEventFocusUrl: getEventFocusUrlArmed,
      onFocusTabChange,
      onPrevTurn,
      onNextTurn,
      onTurnLabelClick,
      onTabSelected,
      onOpenEventFocus,
      isJumpTarget: jumpTargetId
        ? (eventNodeId: string) => eventNodeId === jumpTargetId
        : undefined,
    }),
    [
      onCollapseTranscript,
      getCollapsed,
      getEventUrl,
      linkingEnabled,
      getEventFocusUrlArmed,
      onFocusTabChange,
      onPrevTurn,
      onNextTurn,
      onTurnLabelClick,
      onTabSelected,
      onOpenEventFocus,
      jumpTargetId,
    ]
  );

  // A numbered bar jump must reach the REQUESTED turn even when it's hidden
  // in a user-collapsed region (the bar's range is collapse-independent):
  // scrollToEvent batch-expands the ancestors and retries, and the URL/ring
  // mark the requested anchor. anchorIndexForTurn's previous-visible-anchor
  // fallback stays right for j/k and the header chevrons.
  const onGoToTurnJump = useCallback(
    (n: number) => {
      const anchorId = anchorIdByTurn.get(n);
      if (anchorId !== undefined) {
        // Prime the deep-link dedup (as goToTurn does) so the resulting
        // ?event= change doesn't re-scroll.
        lastScrolledKeyRef.current = `${anchorId}:`;
        scrollToEvent(anchorId);
        onNavigatedToEvent?.(anchorId);
        setJumpTargetId(anchorId);
        return;
      }
      // No model anchor recorded for this number (only possible for a
      // backend-emitted turn span without a model child): previous behavior.
      const idx = anchorIndexForTurn(turnAnchorIds, computedTurnMap, n);
      goToTurn(idx);
      setJumpTargetId(turnAnchorIds[idx] ?? null);
    },
    [
      anchorIdByTurn,
      scrollToEvent,
      onNavigatedToEvent,
      turnAnchorIds,
      computedTurnMap,
      goToTurn,
    ]
  );

  const onFirst = useCallback(() => goToTurn(0), [goToTurn]);
  const onLast = useCallback(
    () => goToTurn(turnAnchorIds.length - 1),
    [goToTurn, turnAnchorIds.length]
  );

  // `f`: focus mode for the current turn — same route derivation as the
  // header control's plain click, keyed off the shared scroll tracker.
  const onFocusCurrentTurn = useMemo(() => {
    if (!getEventFocusUrl || !onOpenEventFocus) return undefined;
    return () => {
      // Already following a running transcript: `f` keeps following AT the
      // newest turn (not the viewport-top turn). Otherwise above turn 1
      // (index -1) clamps to turn 1, mirroring `j`, so `f` never looks dead
      // just because the viewport is on the preamble.
      const following = running && transcriptFollow === true;
      const anchorId = following
        ? turnAnchorIds[turnAnchorIds.length - 1]
        : turnAnchorIds[Math.max(0, currentTurnIndexRef.current)];
      if (!anchorId) return;
      const href = getEventFocusUrlArmed?.(
        anchorId,
        anchorTabsRef.current.get(anchorId)
      );
      if (!href) return;
      onOpenEventFocus(href);
    };
  }, [
    getEventFocusUrl,
    getEventFocusUrlArmed,
    onOpenEventFocus,
    turnAnchorIds,
    running,
    transcriptFollow,
  ]);

  // Track every row, not just turn anchors: computedTurnMap maps tool/content
  // rows to their model's turn after its anchor scrolls out of the DOM.
  const trackedEventIds = useMemo(
    () => flattenedNodes.map((n) => n.id),
    [flattenedNodes]
  );
  // Latches true the first time the user scrolls the container off its very
  // top. Before that, the tracker's mount-time report of the topmost row is a
  // passive load artifact, not a "user is here" signal — see onTopEvent.
  const userScrolledRef = useRef(false);
  const onTopEvent = useCallback(
    (eventId: string) => {
      // A fresh mount reports the topmost row synchronously (useScrollTrack's
      // single checkVisibility). Until the user has actually scrolled off the
      // very top, that report must NOT overwrite the mount seed: in a log whose
      // turn 1 IS the topmost row (no pre-turn preamble) there is no row that
      // maps to "above turn 1" (-1), so an ungated report reads index 0 and the
      // first `j` from load would skip to turn 2. We can't tell -1 from 0 by
      // position at scrollTop 0, so the seed (fresh load: -1; deep link: the
      // target) owns that state until the user engages. Programmatic nav drives
      // currentTurnIndexRef directly (scrollRowToTop's stamp) and moves scrollTop
      // off 0, so genuine reports resume on their own once the user scrolls —
      // and `k` past turn 1 (goToVeryTop) keeps its -1 with no scroll.
      // One-shot latch; no retry loop, no async round trip.
      if ((scrollRef?.current?.scrollTop ?? 0) > 0) {
        userScrolledRef.current = true;
      }
      if (!userScrolledRef.current) return;
      // Same helper as the mount-time seed, so a confirmed report and an
      // unseeded ref can't disagree on what "unknown" (-1) means.
      currentTurnIndexRef.current = anchorIndexForEvent(
        turnAnchorIds,
        computedTurnMap,
        eventId
      );
    },
    [computedTurnMap, turnAnchorIds, scrollRef]
  );
  useScrollTrack(trackedEventIds, onTopEvent, scrollRef, {
    topOffset: offsetTop,
    // The default end-of-viewport advance reports a LATER turn near the end
    // of the log, making `k` oscillate between the last two turns.
    advanceDetectionPointAtEnd: false,
  });

  // Install the scroll tracker before the keyboard listener so its initial
  // synchronous check establishes the current turn before an immediate j/k.
  useTranscriptKeyboardNavigation({
    listHandle,
    scrollRef,
    itemCount: flattenedNodes.length,
    onNext,
    onPrev,
    onPrevAgent,
    onNextAgent,
    onFocusCurrentTurn,
    onFirst: turnAnchorIds.length > 0 ? onFirst : undefined,
    onLast: turnAnchorIds.length > 0 ? onLast : undefined,
    disabled: keyboardNavDisabled,
  });

  // Total turn count (collapse-independent) for the go-to-turn bar's range.
  const firstAnchorId = turnAnchorIds[0];
  const totalTurns =
    (firstAnchorId ? computedTurnMap.get(firstAnchorId)?.totalTurns : 0) ??
    turnAnchorIds.length;

  // Provide the sticky offset (and the scroll container, for the stuck-state
  // observer) to the event-panel sticky headers without threading them through
  // every event-view layer. Memoized so it doesn't churn consumers each render.
  const stickyScroll = useMemo(
    () => ({ stickyTop: offsetTop, scrollRef }),
    [offsetTop, scrollRef]
  );

  return (
    <StickyScrollProvider value={stickyScroll}>
      <div>
        {turnAnchorIds.length > 0 && (
          <GoToTurnBar
            ref={goToTurnBarRef}
            totalTurns={totalTurns}
            offsetTop={offsetTop}
            disabled={keyboardNavDisabled}
            onJump={onGoToTurnJump}
          />
        )}
        <TranscriptVirtualList
          id={id}
          listHandle={listHandle}
          eventNodes={flattenedNodes}
          scrollRef={scrollRef}
          running={running}
          backfilling={backfilling}
          scrollToTopOnFinish={scrollToTopOnFinish}
          className={className}
          initialEventId={scrollEventId}
          navOwned={navOwned}
          followRequested={followRequested}
          renderAgentCard={renderAgentCard}
          turnMap={computedTurnMap}
          eventCallbacks={eventCallbacks}
          eventNodeContext={mergedEventNodeContext}
          selection={rowSelection}
          visibleRangeRef={visibleRangeRef}
        />
      </div>
    </StickyScrollProvider>
  );
});
